/**
 * Inbound 997 functional-acknowledgment display helpers.
 *
 * The raw accept/reject code is an X12 AK5/AK9 value. edi_kleinschmidt_inbound.pl
 * treats A and E as successes and alerts on anything else, so the colouring here
 * follows the same three-way split rather than enumerating every rejection code:
 * accepted (green), accepted-with-errors / partial (amber), rejected (red).
 *
 * Shared by the EDI 997 maintenance grid's detail drawer and the pro History
 * EDI Events tab, which both render the same badge. The LABEL comes from the
 * backend (app/utils/edi_997.py) so the wording has one source of truth.
 */

/** Badge classes for an accept/reject code. Blank code → neutral. */
export const ack997BadgeClass = (code: string | null | undefined): string => {
  const c = (code ?? '').trim().toUpperCase();
  if (c === 'A') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (c === 'E' || c === 'P') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
  if (c) return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
  return 'bg-gray-100 text-label dark:bg-gray-700';
};

/** How a 997 was tied back to the record it acknowledges (cms.edi_997.matchstatus),
 *  spelled out for a tooltip. A 214 ack carries only the pro, so it is matched
 *  POSITIONALLY against the oldest unacknowledged event — worth saying out loud
 *  rather than presenting the guess as a certainty. */
export const ACK997_MATCH_NOTES: Record<string, string> = {
  MATCHED: 'Matched on the reference number we sent.',
  SEQMATCH:
    'Sequence-matched: the 214 acknowledgment carries only the pro, so it was tied to the '
    + 'oldest event for that pro/partner with no acknowledgment yet.',
  AMBIGUOUS:
    'More than one record matched this reference — the oldest unacknowledged one was used.',
  NOTFOUND: 'No record could be matched to this acknowledgment.',
  NOPARTNER: 'No EDI partner is configured for the sender ID on this acknowledgment.',
  SKIPPED: 'This document type is recorded but not matched back to a record.',
};

/** Tooltip for a match status, or '' when there is nothing to explain. */
export const ack997MatchNote = (status: string | null | undefined): string =>
  ACK997_MATCH_NOTES[(status ?? '').trim().toUpperCase()] ?? '';
